import type { CartSuggestionCardsPreviewProduct } from './cart-suggestion-cards-block.config';
import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** Serializable product row used in CB2 kiosk recommendation cards preview scenarios. */
export type KioskCartRecommendationCardsPreviewProduct = CartSuggestionCardsPreviewProduct;

/**
 * Serializable preview VM for `KioskCartRecommendationCardsBlockComponent`.
 * Controls whether the AI recommendation modal is open in CB2 studio preview,
 * and which products to render when open.
 */
export type KioskCartRecommendationCardsBlockScenarioContent = {
  isOpen: boolean;
  suggestions: readonly KioskCartRecommendationCardsPreviewProduct[];
};

export const KIOSKCARTRECOMMENDATIONCARDSBLOCK_LEGACY_FIELD_ORDER = [
  "heading",
  "button_background_color",
  "button_text_color",
  "button_label",
  { type: 'separator' },
  "background_color",
  "text_color",
  { type: 'separator' },
  "card_color",
  "min_recommendations",
  "max_recommendations",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskCartRecommendationCardsBlockDescriptor: _generatedKioskCartRecommendationCardsBlockDescriptor,
  kioskCartRecommendationCardsBlockBlocDefinition,
  kioskCartRecommendationCardsBlockDashboardLayout,
  kioskCartRecommendationCardsBlockConfig,
  kioskCartRecommendationCardsBlockResolveConfig,
  kioskCartRecommendationCardsBlockProperties,
  kioskCartRecommendationCardsBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Kiosk - Cart Recommendation cards",
    componentType: 'KioskCartRecommendationCardsBlockComponent',
    category: 'kiosk',
    description: "Kiosk - Cart Recommendation cards.",
    keywords: ["recommendation", "ai", "suggestion", "cart"],
    allowedIn: [
      { bloc: 'KioskCartPageComponent', slot: 'body' },
    ],


    sharedFields: {}
  },

  () => ({
    heading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.heading.label",
        help: "pages.blocks.kiosk-cart-recommendation-cards.heading.help",
      }
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-600"},
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.button-background-color.label",
      }
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.button-text-color.label",
      }
    }),
    button_label: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.button-label.label",
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.background-color.label",
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#000"},
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.text-color.label",
      }
    }),
    card_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.card-color.label",
      }
    }),
    min_recommendations: propertyValue({
      type: 'number',
      value: null,
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.min-recommendations.label",
        help: "pages.blocks.kiosk-cart-recommendation-cards.min-recommendations.help",
        min: 1,
        step: 1,
      },
    }),
    max_recommendations: propertyValue({
      type: 'number',
      value: null,
      options: {
        label: "pages.blocks.kiosk-cart-recommendation-cards.max-recommendations.label",
        help: "pages.blocks.kiosk-cart-recommendation-cards.max-recommendations.help",
        min: 1,
        step: 1,
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-cart-recommendation-cards.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.kiosk-cart-recommendation-cards.fieldset.heading.label",
            fields: ["heading", "text_color"],
          },
          {
            legend: "pages.blocks.kiosk-cart-recommendation-cards.fieldset.modal.label",
            fields: ["background_color", "card_color"],
          },
          {
            legend: "pages.blocks.kiosk-cart-recommendation-cards.fieldset.button.label",
            fields: ["button_label", "button_background_color", "button_text_color"],
          },
          {
            legend: "pages.blocks.kiosk-cart-recommendation-cards.fieldset.recommendations.label",
            fields: ["min_recommendations", "max_recommendations"],
          },
        ],
      },
    ],
  })
);

export function kioskCartRecommendationCardsBlockDescriptor(): IBlockDescriptor;
export function kioskCartRecommendationCardsBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskCartRecommendationCardsBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskCartRecommendationCardsBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedKioskCartRecommendationCardsBlockDescriptor, kioskCartRecommendationCardsBlockProperties, fieldOrder);
}

export {
  kioskCartRecommendationCardsBlockBlocDefinition,
  kioskCartRecommendationCardsBlockDashboardLayout,
  kioskCartRecommendationCardsBlockProperties,
  kioskCartRecommendationCardsBlockConfig,
  kioskCartRecommendationCardsBlockResolveConfig,
  kioskCartRecommendationCardsBlockDefinitionSetupScenarios,
};

export type KioskCartRecommendationCardsBlockConfig = typeof kioskCartRecommendationCardsBlockConfig;
